import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function Friends({ session }) {
  const [friends, setFriends] = useState([])
  const [pending, setPending] = useState([])
  const [incoming, setIncoming] = useState([])
  const [profiles, setProfiles] = useState({})
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadAll() }, [session])

  const loadAll = async () => {
    setLoading(true)
    const { data: f } = await supabase
      .from('friends')
      .select('*')
      .or(`user_id.eq.${session.user.id},friend_id.eq.${session.user.id}`)

    const accepted = (f || []).filter(r => r.status === 'accepted')
    const sentPending = (f || []).filter(r => r.status === 'pending' && r.user_id === session.user.id)
    const receivedPending = (f || []).filter(r => r.status === 'pending' && r.friend_id === session.user.id)

    setFriends(accepted)
    setPending(sentPending)
    setIncoming(receivedPending)

    const ids = new Set()
    f?.forEach(r => { ids.add(r.user_id); ids.add(r.friend_id) })
    if (ids.size > 0) {
      const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name, username').in('id', [...ids])
      const pmap = {}
      prof?.forEach(p => { pmap[p.id] = p })
      setProfiles(pmap)
    }
    setLoading(false)
  }

  const handleSearch = async (q) => {
    setSearch(q)
    if (q.length < 2) { setSearchResults([]); return }
    const { data } = await supabase.from('profiles').select('id, first_name, last_name, username')
      .neq('id', session.user.id)
      .or(`username.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
      .limit(8)
    setSearchResults(data || [])
  }

  const sendRequest = async (p) => {
    await supabase.from('friends').insert({ user_id: session.user.id, friend_id: p.id, status: 'pending' })
    setSearch('')
    setSearchResults([])
    loadAll()
  }

  const cancelRequest = async (r) => {
    await supabase.from('friends').delete().eq('id', r.id)
    loadAll()
  }

  const acceptRequest = async (r) => {
    await supabase.from('friends').update({ status: 'accepted' }).eq('id', r.id)
    loadAll()
  }

  const declineRequest = async (r) => {
    await supabase.from('friends').update({ status: 'declined' }).eq('id', r.id)
    loadAll()
  }

  const removeFriend = async (r) => {
    await supabase.from('friends').delete().eq('id', r.id)
    loadAll()
  }

  const getName = (id) => {
    const p = profiles[id]
    return p ? `${p.first_name} ${p.last_name || ''}`.trim() : '?'
  }

  const getUsername = (id) => profiles[id]?.username || ''

  const alreadyFriend = (id) => {
    return [...friends, ...pending].some(r => r.user_id === id || r.friend_id === id)
  }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      <div style={s.body}>

        {/* Search */}
        <div style={s.sectionTitle}>ADD FRIEND</div>
        <input style={s.input} placeholder="Search by name or @username"
          value={search} onChange={e => handleSearch(e.target.value)} />
        {searchResults.length > 0 && (
          <div style={s.searchResults}>
            {searchResults.map(p => (
              <div key={p.id} style={s.searchRow}>
                <div style={s.searchInfo}>
                  <div style={s.searchName}>{p.first_name} {p.last_name}</div>
                  <div style={s.searchUser}>@{p.username}</div>
                </div>
                {alreadyFriend(p.id) ? (
                  <div style={s.alreadyTag}>Added</div>
                ) : (
                  <button style={s.addBtn} onClick={() => sendRequest(p)}>+ Add</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Incoming requests */}
        {incoming.length > 0 && (
          <>
            <div style={s.sectionTitle}>FRIEND REQUESTS</div>
            {incoming.map(r => (
              <div key={r.id} style={s.friendRow}>
                <div style={s.friendInfo}>
                  <div style={s.friendName}>{getName(r.user_id)}</div>
                  <div style={s.friendUser}>@{getUsername(r.user_id)}</div>
                </div>
                <div style={s.rowBtns}>
                  <button style={s.acceptBtn} onClick={() => acceptRequest(r)}>Accept</button>
                  <button style={s.declineBtn} onClick={() => declineRequest(r)}>Decline</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Pending sent */}
        {pending.length > 0 && (
          <>
            <div style={s.sectionTitle}>PENDING</div>
            {pending.map(r => (
              <div key={r.id} style={s.friendRow}>
                <div style={s.friendInfo}>
                  <div style={s.friendName}>{getName(r.friend_id)}</div>
                  <div style={s.friendUser}>@{getUsername(r.friend_id)}</div>
                </div>
                <button style={s.cancelBtn} onClick={() => cancelRequest(r)}>Cancel</button>
              </div>
            ))}
          </>
        )}

        {/* Friends list */}
        <div style={s.sectionTitle}>MY FRIENDS {friends.length > 0 ? `(${friends.length})` : ''}</div>
        {friends.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>👥</div>
            <div style={s.emptyText}>No friends yet</div>
            <div style={s.emptySub}>Search for people you know and send them a request</div>
          </div>
        ) : friends.map(r => {
          const otherId = r.user_id === session.user.id ? r.friend_id : r.user_id
          return (
            <div key={r.id} style={s.friendRow}>
              <div style={s.friendInfo}>
                <div style={s.friendName}>{getName(otherId)}</div>
                <div style={s.friendUser}>@{getUsername(otherId)}</div>
              </div>
              <button style={s.removeBtn} onClick={() => removeFriend(r)}>Remove</button>
            </div>
          )
        })}

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: '#7A7570', letterSpacing: 1.5, marginTop: 8 },
  input: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  searchResults: { background: '#FFFFFF', borderRadius: 16, border: '1px solid #E0DDD4', overflow: 'hidden' },
  searchRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #F0EDE4' },
  searchInfo: { flex: 1 },
  searchName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  searchUser: { fontSize: 12, color: '#7A7570' },
  addBtn: { background: '#00563F', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  alreadyTag: { fontSize: 11, color: '#7A7570', fontWeight: 600 },
  friendRow: { background: '#FFFFFF', borderRadius: 16, border: '1px solid #E0DDD4', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  friendInfo: { flex: 1 },
  friendName: { fontSize: 15, fontWeight: 700, color: '#1A1814' },
  friendUser: { fontSize: 12, color: '#7A7570', marginTop: 2 },
  rowBtns: { display: 'flex', gap: 8 },
  acceptBtn: { background: '#00563F', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  declineBtn: { background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  cancelBtn: { background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  removeBtn: { background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit' },
  empty: { background: '#FFFFFF', borderRadius: 20, padding: 28, border: '1px solid #E0DDD4', textAlign: 'center' },
  emptyIcon: { fontSize: 32, marginBottom: 8 },
  emptyText: { fontSize: 15, fontWeight: 700, color: '#1A1814', marginBottom: 4 },
  emptySub: { fontSize: 13, color: '#7A7570' },
}
